import React, { useRef, useState } from "react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";

import SearchableDropdownItem, { SearchableListItem } from "./SearchableDropdownItem";
import Spinner from "../Spinner";
import { DoubleChevronDownIcon, SearchIcon } from "../../icons";

type SearchableDropdownProps<T extends string> = {
  label?: string;
  items: SearchableListItem<T>[];
  selectedItem: SearchableListItem<T> | undefined;
  setSelectedItem: React.Dispatch<any>;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  emptyText?: string;
  subtext?: string;
};

const DEFAULT_LOADING_TEXT = "Loading options...";

const SearchableDropdown = ({
  label,
  items,
  selectedItem,
  setSelectedItem = () => undefined,
  className = "",
  disabled = false,
  isLoading = false,
  loadingText = DEFAULT_LOADING_TEXT,
  emptyText,
  subtext,
}: SearchableDropdownProps<string>) => {
  const [query, setQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const SelectedLogo = selectedItem?.icon;
  const labelId = React.useId();

  const filteredItems =
    query === "" ? items : items.filter((item) => item.searchValue.toLowerCase().includes(query.toLowerCase()));
  const shouldShowStatus = isLoading || (items.length === 0 && emptyText !== undefined);

  return (
    <div className={className}>
      {label && (
        <label id={labelId} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      {shouldShowStatus ? (
        <div className="relative w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-left text-sm text-gray-500 shadow-sm">
          {isLoading ? (
            <span className="flex items-center gap-2">
              <Spinner.Inline className="h-4 w-4" />
              <span>{loadingText}</span>
            </span>
          ) : (
            emptyText
          )}
        </div>
      ) : (
        <div className="relative">
          <Listbox
            value={selectedItem ?? null}
            onChange={(item) => {
              setSelectedItem(item);
              setQuery("");
            }}
            disabled={disabled}
          >
            {({ open }) => (
              <>
                <ListboxButton
                  aria-labelledby={label ? labelId : undefined}
                  data-state={open ? "open" : "closed"}
                  className={`relative w-full rounded-md pl-3 pr-8 py-2 text-left sm:text-sm focus:outline-none disabled:bg-gray-50 disabled:text-gray-500 border border-gray-300 shadow-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white ${
                    disabled ? "pointer-events-none" : "cursor-pointer"
                  }`}
                >
                  <span className="flex items-center">
                    {SelectedLogo && <SelectedLogo className="flex-shrink-0 h-6 w-6 rounded-full mr-3" />}
                    <span className="block truncate">
                      {selectedItem?.text ? (
                        selectedItem.text
                      ) : (
                        <span className="text-gray-400 text-sm italic">Select an option</span>
                      )}
                    </span>
                  </span>
                  <span className="ml-3 absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <DoubleChevronDownIcon className="h-5 w-5 text-gray-400" />
                  </span>
                </ListboxButton>

                <Transition
                  show={open}
                  enter=""
                  enterFrom=""
                  enterTo=""
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterEnter={() => searchInputRef.current?.focus()}
                >
                  <div className="z-10 absolute mt-1 w-auto min-w-full rounded-md bg-white shadow-lg ring-1 ring-black/5">
                    <div className="relative border-b border-gray-200 px-3 py-2">
                      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="w-full bg-transparent pl-5 text-sm placeholder:text-gray-400 outline-none ring-0 border-0 shadow-none focus:ring-0 focus:border-0 focus:shadow-none p-0"
                        placeholder="Search..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                    <ListboxOptions
                      as="ul"
                      static
                      className="max-h-56 rounded-b-md py-1 text-base overflow-auto focus:outline-none sm:text-sm"
                      modal={false}
                    >
                      {filteredItems.length === 0 ? (
                        <li className="relative cursor-default select-none py-2 px-3 text-gray-500 text-sm">
                          No matches found.
                        </li>
                      ) : (
                        filteredItems.map((item) => (
                          <ListboxOption
                            as="li"
                            key={item.key}
                            value={item}
                            disabled={item.unavailable}
                            className={({ selected }) =>
                              `${selected ? "bg-gray-50" : ""} text-gray-900 cursor-pointer select-none relative py-2 pl-3 pr-6 hover:bg-gray-50`
                            }
                          >
                            {({ selected }) => <SearchableDropdownItem isSelected={selected} item={item} />}
                          </ListboxOption>
                        ))
                      )}
                    </ListboxOptions>
                  </div>
                </Transition>
              </>
            )}
          </Listbox>
        </div>
      )}
      {subtext && (
        <p className="mt-2 text-xs text-gray-400 whitespace-pre-line" id={`${labelId}-description`}>
          {subtext}
        </p>
      )}
    </div>
  );
};

export type SearchableDropdownListItem<T extends string> = SearchableListItem<T>;
export default SearchableDropdown;
